import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { FolderKanban, X, Shield, Cpu, BookOpen, ChevronRight, ChevronDown, ExternalLink, Info, Layers, Users, Copy, Check, FileCode } from 'lucide-react'
import { api } from '../api/client'
import { Card } from '../components/ui/Card'
import { Button } from '../components/ui/Button'
import type { Project, CatalogItem } from '../api/types'

// ─── Constants ────────────────────────────────────────────────────────────────

const CANONICAL_REPO_URL = 'https://github.com/kone-rnd/digital-innovation-sdlc-ai-baseline'
const CANONICAL_PATH = 'sdlc-canonical'

const canonicalTreeUrl = (path = '') => {
  const suffix = path ? `/${path}` : ''
  return `${CANONICAL_REPO_URL}/tree/main/${CANONICAL_PATH}${suffix}`
}

// ─── Agent Detail Modal ───────────────────────────────────────────────────────

type DetailTab = 'content' | 'skills' | 'rules'

function AgentDetailModal({ agentId, onClose }: Readonly<{ agentId: string; onClose: () => void }>) {
  const [activeTab, setActiveTab] = useState<DetailTab>('content')

  const { data: agentDetail } = useQuery({
    queryKey: ['agent-detail', agentId],
    queryFn: () => api.registry.getAgent(agentId),
    staleTime: 60_000,
  })

  const linkedSkillIds = agentDetail?.uses_skills ?? []
  const { data: linkedSkillDetails } = useQuery({
    queryKey: ['linked-skills', linkedSkillIds.join(',')],
    queryFn: () => Promise.all(linkedSkillIds.map(id => api.registry.getSkill(id).catch(() => null))),
    enabled: linkedSkillIds.length > 0,
    staleTime: 60_000,
  })

  const tabs: { key: DetailTab; label: string; icon: React.ElementType; show: boolean }[] = [
    { key: 'content', label: 'Persona', icon: Cpu, show: true },
    { key: 'skills', label: `Skills (${linkedSkillIds.length})`, icon: BookOpen, show: linkedSkillIds.length > 0 },
    { key: 'rules', label: 'Rules', icon: Shield, show: (agentDetail?.uses_rules?.length ?? 0) > 0 },
  ]

  return (
    // eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions
    <div
      onClick={onClose}
      style={{
        position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.8)',
        display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1100,
      }}
    >
      {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions */}
      <div
        onClick={e => e.stopPropagation()}
        style={{
          background: 'var(--bg)', border: '1px solid var(--border)', borderRadius: 'var(--radius-m)',
          width: '100%', maxWidth: 800, maxHeight: '85vh', display: 'flex', flexDirection: 'column',
        }}
      >
        {/* Header */}
        <div style={{
          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
          padding: 'var(--space-3) var(--space-4)', borderBottom: '1px solid var(--border)',
        }}>
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <Cpu size={18} color="var(--brand)" />
              <h2 style={{ fontSize: 18, fontWeight: 700, margin: 0 }}>{agentDetail?.name || agentId}</h2>
              <span style={{ fontSize: 12, color: 'var(--text-muted)', background: 'var(--surface-hover)', padding: '2px 8px', borderRadius: 4 }}>{agentId}</span>
            </div>
            {agentDetail?.role && (
              <p style={{ fontSize: 13, color: 'var(--text-secondary)', margin: '4px 0 0 28px' }}>{agentDetail.role}</p>
            )}
          </div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)', padding: 4 }}>
            <X size={20} />
          </button>
        </div>

        {/* Sub-tabs */}
        <div style={{ display: 'flex', gap: 0, borderBottom: '1px solid var(--border)', paddingLeft: 'var(--space-4)' }}>
          {tabs.filter(t => t.show).map(t => (
            <button
              key={t.key}
              onClick={() => setActiveTab(t.key)}
              style={{
                display: 'flex', alignItems: 'center', gap: 6,
                padding: '10px 16px', border: 'none', cursor: 'pointer',
                background: 'transparent', fontSize: 13, fontWeight: 500,
                color: activeTab === t.key ? 'var(--brand)' : 'var(--text-muted)',
                borderBottom: activeTab === t.key ? '2px solid var(--brand)' : '2px solid transparent',
              }}
            >
              <t.icon size={13} />
              {t.label}
            </button>
          ))}
        </div>

        {/* Content */}
        <div style={{ flex: 1, overflowY: 'auto', padding: 'var(--space-3) var(--space-4)' }}>
          {activeTab === 'content' && (
            <div>
              {!agentDetail?.persona && (
                <div style={{ color: 'var(--text-muted)', fontSize: 13, padding: 'var(--space-3)' }}>Loading content…</div>
              )}
              {agentDetail?.persona && (
                <pre style={{
                  whiteSpace: 'pre-wrap', wordWrap: 'break-word',
                  fontFamily: 'var(--font-mono, monospace)', fontSize: 13,
                  lineHeight: 1.6, color: 'var(--text-primary)',
                  background: 'var(--surface)', padding: 'var(--space-3)',
                  borderRadius: 'var(--radius-s)', border: '1px solid var(--border)',
                  margin: 0,
                }}>
                  {agentDetail.persona}
                </pre>
              )}
              {agentDetail?.has_checklist && agentDetail.checklist && (
                <div style={{ marginTop: 'var(--space-3)' }}>
                  <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 8, color: 'var(--text-secondary)' }}>Startup Checklist</div>
                  <pre style={{
                    whiteSpace: 'pre-wrap', wordWrap: 'break-word',
                    fontFamily: 'var(--font-mono, monospace)', fontSize: 12,
                    lineHeight: 1.5, color: 'var(--text-secondary)',
                    background: 'var(--surface-hover)', padding: 'var(--space-2)',
                    borderRadius: 'var(--radius-s)', border: '1px solid var(--border)',
                    margin: 0,
                  }}>
                    {agentDetail.checklist}
                  </pre>
                </div>
              )}
            </div>
          )}

          {activeTab === 'skills' && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-2)' }}>
              {linkedSkillIds.map((skillId, idx) => {
                const detail = linkedSkillDetails?.[idx]
                return (
                  <div key={skillId} style={{ border: '1px solid var(--border)', borderRadius: 'var(--radius-s)', overflow: 'hidden' }}>
                    <div style={{
                      padding: '10px 14px', background: 'var(--surface)',
                      borderBottom: '1px solid var(--border)',
                      display: 'flex', alignItems: 'center', gap: 8,
                    }}>
                      <BookOpen size={14} color="var(--success)" />
                      <span style={{ fontWeight: 600, fontSize: 13 }}>{detail?.title || skillId}</span>
                      <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>{skillId}</span>
                    </div>
                    <div style={{ padding: '12px 14px' }}>
                      {detail ? (
                        <pre style={{
                          whiteSpace: 'pre-wrap', wordWrap: 'break-word',
                          fontFamily: 'var(--font-mono, monospace)', fontSize: 12,
                          lineHeight: 1.5, color: 'var(--text-secondary)',
                          margin: 0, maxHeight: 300, overflowY: 'auto',
                        }}>
                          {detail.content}
                        </pre>
                      ) : (
                        <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>Loading…</span>
                      )}
                    </div>
                  </div>
                )
              })}
            </div>
          )}

          {activeTab === 'rules' && agentDetail?.uses_rules && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {agentDetail.uses_rules.map(ruleId => (
                <div key={ruleId} style={{
                  padding: '8px 12px', background: 'var(--surface)',
                  borderRadius: 'var(--radius-s)', border: '1px solid var(--border)',
                  display: 'flex', alignItems: 'center', gap: 8,
                }}>
                  <Shield size={13} color="var(--warning)" />
                  <span style={{ fontWeight: 500, fontSize: 13 }}>{ruleId}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

// ─── AGENTS.md copy-ready block ───────────────────────────────────────────────

const AGENTS_MD_TEMPLATE = (projectId: string, name: string) => `---
project: ${projectId}
---
# ${name}

These steps are mandatory. Do them BEFORE composing any response.

### Step 1 — Bootstrap (once per session)

At the start of every conversation, **always** call the sdlc-platform MCP:

1. Run \`git remote get-url origin\` to obtain \`repo_remote\`.
2. Call \`get_startup_context(repo_path=<absolute path to this repository>, repo_remote=<git remote get-url origin output>)\` as your **VERY FIRST** tool call — before reading any files, before responding to the user.
- \`repo_remote\` is required. Pass \`""\` only if the repo has no remote or the command fails; never omit it.
- Read \`session_id\` from the response and reuse it in **every** subsequent MCP call.
- Read \`governing_rules.mandatory\` and apply every rule immediately.

> **Note on IDE wiring files** (\`.kiro/agents/kone-context-lake.md\`, \`.github/agents/kone-context-lake.agent.md\`, \`.claude/agents/kone-context-lake.md\`): these are lightweight routing configs, **not** a substitute for calling the MCP. Their presence means the repo is set up correctly — you still MUST call \`get_startup_context\` on every session start.

### Step 2 — Create missing IDE wiring files

After \`get_startup_context\` returns, check \`setup.ide_files\` in the response:

- For each file listed: if it does **not** exist at the given path → create it with the provided content.
- If it already exists → skip (never overwrite).
- Create files for ALL IDEs listed, not just the current one.
- Delete any stale files listed in \`setup.instruction\`.

### Step 3 — Get context before every response

For **every** user message (not just the first), before composing a response:

1. Call \`get_context(session_id, task=<user's message>)\`.
2. Read \`required_next_step\` from the response and call the indicated agent (e.g. \`get_agent(agent_id, session_id)\`).
3. Apply the agent persona and follow its output format.
4. Only then compose and send your response.
`

function AgentsMdBlock({ projectId, name }: Readonly<{ projectId: string; name: string }>) {
  const [copied, setCopied] = useState(false)
  const [expanded, setExpanded] = useState(false)
  const content = AGENTS_MD_TEMPLATE(projectId, name)

  function copy() {
    navigator.clipboard.writeText(content)
    setCopied(true)
    setTimeout(() => setCopied(false), 2000)
  }

  return (
    <div style={{
      marginBottom: 'var(--space-3)', border: '1px solid var(--border)',
      borderRadius: 'var(--radius-s)', overflow: 'hidden',
    }}>
      <button
        onClick={() => setExpanded(!expanded)}
        style={{
          display: 'flex', alignItems: 'center', gap: 8, width: '100%',
          padding: '10px 12px', background: 'var(--surface)', border: 'none',
          cursor: 'pointer', color: 'var(--text-primary)',
        }}
      >
        <FileCode size={14} color="var(--brand)" />
        <span style={{ fontSize: 13, fontWeight: 600, flex: 1, textAlign: 'left' }}>
          AGENTS.md — ready to commit
        </span>
        <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>
          {expanded ? 'collapse' : 'expand'}
        </span>
        <ChevronRight size={14} color="var(--text-muted)" style={{ transform: expanded ? 'rotate(90deg)' : 'none', transition: '0.15s' }} />
      </button>
      {expanded && (
        <div style={{ padding: '0 12px 12px', background: 'var(--surface)' }}>
          <div style={{ fontSize: 11, color: 'var(--text-muted)', marginBottom: 6 }}>
            Copy this file to the <strong>root</strong> of your repository. All values are pre-filled for <strong>{name}</strong>.
          </div>
          <div style={{ position: 'relative' }}>
            <pre style={{
              background: 'var(--surface-hover)', padding: '10px 12px',
              borderRadius: 'var(--radius-xs)', fontSize: 11, overflow: 'auto',
              border: '1px solid var(--border)', margin: 0, lineHeight: 1.6,
              maxHeight: 200,
            }}>
              {content}
            </pre>
            <button
              onClick={copy}
              style={{
                position: 'absolute', top: 8, right: 8,
                background: copied ? 'var(--success)' : 'var(--brand)', color: '#fff',
                border: 'none', borderRadius: 4, padding: '4px 10px',
                cursor: 'pointer', fontSize: 11, display: 'flex', alignItems: 'center', gap: 4,
              }}
            >
              {copied ? <><Check size={11} /> Copied</> : <><Copy size={11} /> Copy</>}
            </button>
          </div>
        </div>
      )}
    </div>
  )
}

// ─── Project Detail Panel (read-only) ─────────────────────────────────────────────

export function SystemDetail({ project, onClose }: Readonly<{ project: Project; onClose: () => void }>) {
  const systemId = project.project_id || project.id
  const [viewingAgent, setViewingAgent] = useState<string | null>(null)

  const agents = project.selected_agents || project.agents || []
  const skills = project.selected_skills || project.skills || []
  const profile = project.profile || (project.profiles?.[0]) || '—'

  // Fetch catalog to resolve agent names/descriptions
  const { data: catalog } = useQuery({
    queryKey: ['catalog'],
    queryFn: () => api.registry.getCatalog(),
    staleTime: 60_000,
  })

  return (
    <div style={{
      position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.85)',
      display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000,
    }}>
      <div style={{
        background: 'var(--bg)', border: '1px solid var(--border)', borderRadius: 'var(--radius-m)',
        padding: 'var(--space-4)', width: '100%', maxWidth: 680, maxHeight: '90vh', overflowY: 'auto',
      }}>
        {/* Header */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 'var(--space-3)' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <FolderKanban size={18} color="var(--brand)" />
            <h2 style={{ fontSize: 18, fontWeight: 700 }}>{project.name}</h2>
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)' }}>
              <X size={18} />
            </button>
          </div>
        </div>

        {/* Project Info */}
        <div style={{ marginBottom: 'var(--space-3)', padding: 'var(--space-2)', background: 'var(--surface)', borderRadius: 'var(--radius-s)' }}>
          <div style={{ fontSize: 13 }}>
            {project.purpose && <div style={{ marginBottom: 8, color: 'var(--text-secondary)' }}>{project.purpose}</div>}
            <div style={{ marginBottom: 4 }}><strong>Profile:</strong>{' '}
              <span style={{
                padding: '2px 8px', fontSize: 11, borderRadius: 4,
                background: 'var(--brand-subtle)', color: 'var(--brand)',
              }}>{profile}</span>
            </div>
            {project.team && <div style={{ marginBottom: 4 }}><strong>Team:</strong> {project.team}</div>}
            {project.repos && project.repos.length > 0 && (
              <div style={{ marginBottom: 4 }}><strong>Repos:</strong> {project.repos.join(', ')}</div>
            )}
            {project.domain && <div style={{ marginBottom: 4 }}><strong>Domain:</strong> {project.domain}</div>}
            {project.tier && <div style={{ marginBottom: 4 }}><strong>Tier:</strong> {project.tier}</div>}
            {project.description && <div style={{ color: 'var(--text-muted)', marginTop: 6 }}>{project.description}</div>}
          </div>
        </div>

        {/* Agents list */}
        {agents.length > 0 && (
          <div style={{ marginBottom: 'var(--space-3)' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 8 }}>
              <Cpu size={13} color="var(--brand)" />
              <span style={{ fontSize: 13, fontWeight: 600 }}>Agents ({agents.length})</span>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
              {agents.map(agentId => {
                const agentInfo = catalog?.agents?.find((a: CatalogItem) => a.id === agentId)
                return (
                  <button
                    key={agentId}
                    onClick={() => setViewingAgent(agentId)}
                    style={{
                      display: 'flex', alignItems: 'center', gap: 8,
                      padding: '6px 10px', background: 'var(--surface)',
                      border: '1px solid var(--border)', borderRadius: 'var(--radius-s)',
                      cursor: 'pointer', textAlign: 'left', width: '100%',
                      transition: 'border-color 0.15s',
                    }}
                    onMouseEnter={e => (e.currentTarget.style.borderColor = 'var(--brand)')}
                    onMouseLeave={e => (e.currentTarget.style.borderColor = 'var(--border)')}
                  >
                    <Cpu size={13} color="var(--brand)" />
                    <div style={{ flex: 1 }}>
                      <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-primary)' }}>
                        {agentInfo?.name || agentId}
                      </div>
                      {agentInfo?.description && (
                        <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 1 }}>
                          {agentInfo.description}
                        </div>
                      )}
                    </div>
                    <ChevronRight size={12} color="var(--text-muted)" />
                  </button>
                )
              })}
            </div>
          </div>
        )}

        {/* Skills list */}
        {skills.length > 0 && (
          <div style={{ marginBottom: 'var(--space-3)' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 8 }}>
              <BookOpen size={13} color="var(--success)" />
              <span style={{ fontSize: 13, fontWeight: 600 }}>Skills ({skills.length})</span>
            </div>
            <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
              {skills.map(skillId => (
                <span key={skillId} style={{
                  padding: '3px 10px', fontSize: 11, borderRadius: 4,
                  background: 'var(--surface)', border: '1px solid var(--border)',
                  color: 'var(--text-secondary)',
                }}>{skillId}</span>
              ))}
            </div>
          </div>
        )}

        {/* Capabilities */}
        {project.capabilities && project.capabilities.length > 0 && (
          <div style={{ marginBottom: 'var(--space-3)' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 8 }}>
              <Layers size={13} color="var(--warning)" />
              <span style={{ fontSize: 13, fontWeight: 600 }}>Capabilities</span>
            </div>
            <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
              {project.capabilities.map(cap => (
                <span key={cap} style={{
                  padding: '3px 10px', fontSize: 11, borderRadius: 4,
                  background: 'var(--surface-hover)', color: 'var(--text-secondary)',
                }}>{cap}</span>
              ))}
            </div>
          </div>
        )}

        {/* IDE connection */}
        <section style={{ marginBottom: 'var(--space-3)' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 8 }}>
            <ChevronRight size={13} color="var(--brand)" />
            <span style={{ fontSize: 13, fontWeight: 600 }}>Connect your IDE</span>
            <a
              href="/mcp-registry#sdlc-platform"
              aria-label="View KONE SDLC Platform in MCP Registry"
              style={{
                marginLeft: 'auto', color: 'var(--brand)', display: 'inline-flex',
                alignItems: 'center', gap: 2, fontSize: 12, textDecoration: 'none',
              }}
            >
              View in MCP Registry <ChevronRight size={12} />
            </a>
          </div>
        </section>

        {/* Ready-to-copy AGENTS.md */}
        <AgentsMdBlock projectId={systemId} name={project.name} />

        {/* Source info */}
        <div style={{
          display: 'flex', alignItems: 'center', gap: 6, padding: 'var(--space-2)',
          background: 'var(--surface)', borderRadius: 'var(--radius-s)', fontSize: 12,
          color: 'var(--text-muted)',
        }}>
          <Info size={12} />
          <span>
            This project is defined in Git. To modify, open an MR against{' '}
            <a href={canonicalTreeUrl(`systems/${systemId}/system.yaml`)} target="_blank" rel="noopener noreferrer" style={{ color: 'var(--brand)' }}>
              systems/{systemId}/system.yaml
            </a>
          </span>
        </div>

        {/* Close button */}
        <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 'var(--space-3)' }}>
          <Button variant="ghost" onClick={onClose}>Close</Button>
        </div>
      </div>

      {/* Agent Detail Modal */}
      {viewingAgent && <AgentDetailModal agentId={viewingAgent} onClose={() => setViewingAgent(null)} />}
    </div>
  )
}

// ─── Main Page ────────────────────────────────────────────────────────────────

export function MyProjects() {
  const [selectedProject, setSelectedProject] = useState<Project | null>(null)

  const { data: projectsData } = useQuery({
    queryKey: ['projects'],
    queryFn: () => api.projects.list(),
    refetchInterval: 30_000,
  })

  const projects = projectsData?.projects ?? []

  return (
    <div style={{ padding: 'var(--space-4)', maxWidth: 1200, margin: '0 auto' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 'var(--space-4)' }}>
        <div>
          <h1 style={{ fontSize: 24, fontWeight: 700, marginBottom: 4 }}>Projects</h1>
          <p style={{ color: 'var(--text-secondary)', fontSize: 14 }}>
            Registered projects from the canonical repository. Changes are made via Git MRs.
          </p>
        </div>
        <a
          href={canonicalTreeUrl('systems')}
          target="_blank"
          rel="noopener noreferrer"
          style={{
            display: 'flex', alignItems: 'center', gap: 6, padding: '8px 14px',
            background: 'var(--surface)', border: '1px solid var(--border)',
            borderRadius: 'var(--radius-s)', color: 'var(--text-primary)',
            textDecoration: 'none', fontSize: 13, fontWeight: 500,
          }}
        >
          <ExternalLink size={14} /> View in Git
        </a>
      </div>

      {projects.length === 0 ? (
        <Card>
          <div style={{ textAlign: 'center', padding: 'var(--space-5)', color: 'var(--text-muted)' }}>
            <FolderKanban size={40} style={{ margin: '0 auto 12px', opacity: 0.4 }} />
            <div style={{ fontSize: 16, fontWeight: 600, marginBottom: 8 }}>No projects registered yet</div>
            <div style={{ fontSize: 14, marginBottom: 'var(--space-3)' }}>
              Add a project by creating a <code>systems/&lt;name&gt;/system.yaml</code> in the canonical repo.
            </div>
            <a
              href={canonicalTreeUrl('systems/_template')}
              target="_blank"
              rel="noopener noreferrer"
              style={{ color: 'var(--brand)', display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 13 }}
            >
              View template <ExternalLink size={12} />
            </a>
          </div>
        </Card>
      ) : (
        <TeamGroupedProjects projects={projects} onSelect={setSelectedProject} />
      )}

      {selectedProject && <SystemDetail project={selectedProject} onClose={() => setSelectedProject(null)} />}
    </div>
  )
}

// ─── Team Grouped Projects ────────────────────────────────────────────────────

function TeamGroupedProjects({ projects, onSelect }: Readonly<{ projects: Project[]; onSelect: (p: Project) => void }>) {
  // Group by team
  const grouped = new Map<string, Project[]>()
  for (const project of projects) {
    const team = project.team || 'Unassigned'
    const list = grouped.get(team) ?? []
    list.push(project)
    grouped.set(team, list)
  }
  const teamEntries = Array.from(grouped.entries()).sort((a, b) => a[0].localeCompare(b[0]))

  // Default: all teams collapsed
  const [collapsedTeams, setCollapsedTeams] = useState<Set<string>>(() => new Set(teamEntries.map(([t]) => t)))

  function toggleTeam(team: string) {
    setCollapsedTeams(prev => {
      const next = new Set(prev)
      if (next.has(team)) next.delete(team)
      else next.add(team)
      return next
    })
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-3)' }}>
      {teamEntries.map(([team, teamProjects]) => (
        <div key={team}>
          <button
            onClick={() => toggleTeam(team)}
            style={{
              display: 'flex', alignItems: 'center', gap: 8, width: '100%',
              background: 'none', border: 'none', cursor: 'pointer', padding: '8px 0',
              color: 'var(--text-primary)', marginBottom: 'var(--space-1)',
            }}
          >
            {collapsedTeams.has(team)
              ? <ChevronRight size={14} color="var(--text-muted)" />
              : <ChevronDown size={14} color="var(--text-muted)" />}
            <Users size={15} color="var(--brand)" />
            <span style={{ fontSize: 15, fontWeight: 600 }}>{team}</span>
            <span style={{
              fontSize: 11, color: 'var(--text-muted)', background: 'var(--surface-hover)',
              padding: '2px 8px', borderRadius: 10,
            }}>{teamProjects.length} project{teamProjects.length > 1 ? 's' : ''}</span>
          </button>
          {!collapsedTeams.has(team) && (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))', gap: 'var(--space-3)', paddingLeft: 'var(--space-2)' }}>
              {teamProjects.map((project, i) => {
                const agents = project.selected_agents || project.agents || []
                const skills = project.selected_skills || project.skills || []
                const profile = project.profile || (project.profiles?.[0]) || ''
                return (
                  <Card
                    key={project.project_id ?? project.id ?? i}
                    style={{ cursor: 'pointer', transition: 'border-color 0.15s', position: 'relative' }}
                    onClick={() => onSelect(project)}
                  >
                    {/* Header */}
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 6 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <div style={{ width: 32, height: 32, borderRadius: 'var(--radius-s)', background: 'var(--brand-subtle)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                          <FolderKanban size={16} color="var(--brand)" />
                        </div>
                        <div>
                          <div style={{ fontWeight: 600, fontSize: 14 }}>{project.name || `System ${i + 1}`}</div>
                        </div>
                      </div>
                      <ChevronRight size={14} color="var(--text-muted)" />
                    </div>

                    {/* Profile badge */}
                    {profile && (
                      <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap', marginBottom: 10 }}>
                        <span style={{
                          padding: '2px 8px', fontSize: 11, borderRadius: 10,
                          background: 'var(--brand-subtle)', color: 'var(--brand)', fontWeight: 500,
                        }}>{profile}</span>
                      </div>
                    )}

                    {/* Stats row */}
                    <div style={{
                      display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 8,
                      padding: '10px 0', borderTop: '1px solid var(--border)',
                    }}>
                      <div style={{ textAlign: 'center' }}>
                        <div style={{ fontSize: 16, fontWeight: 700, color: 'var(--warning)' }}>
                          <Shield size={12} style={{ verticalAlign: 'middle', marginRight: 2 }} />
                          1
                        </div>
                        <div style={{ fontSize: 10, color: 'var(--text-muted)' }}>profile</div>
                      </div>
                      <div style={{ textAlign: 'center' }}>
                        <div style={{ fontSize: 16, fontWeight: 700, color: 'var(--brand)' }}>
                          <Cpu size={12} style={{ verticalAlign: 'middle', marginRight: 2 }} />
                          {agents.length}
                        </div>
                        <div style={{ fontSize: 10, color: 'var(--text-muted)' }}>agents</div>
                      </div>
                      <div style={{ textAlign: 'center' }}>
                        <div style={{ fontSize: 16, fontWeight: 700, color: 'var(--success)' }}>
                          <BookOpen size={12} style={{ verticalAlign: 'middle', marginRight: 2 }} />
                          {skills.length}
                        </div>
                        <div style={{ fontSize: 10, color: 'var(--text-muted)' }}>skills</div>
                      </div>
                    </div>

                    {/* Purpose snippet */}
                    {project.purpose && (
                      <div style={{
                        paddingTop: 8, borderTop: '1px solid var(--border)',
                        fontSize: 11, color: 'var(--text-muted)', lineHeight: 1.4,
                        overflow: 'hidden', textOverflow: 'ellipsis',
                        display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical',
                      }}>
                        {project.purpose}
                      </div>
                    )}
                  </Card>
                )
              })}
            </div>
          )}
        </div>
      ))}
    </div>
  )
}
